import React from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, Megaphone, TrendingUp, Search, Target, BarChart3, Globe2 } from "lucide-react";
import { SearchWidget } from "@/components/SearchWidget";
import { Button } from "@/components/ui/button";

const CONTENT = {
  about: { title: "About World Tour Hub", body: [
    "World Tour Hub Ltd is a next-generation online travel agency built to make booking the entire journey — flights, hotels, holiday packages and car hire — effortless, transparent and affordable.",
    "Founded on the belief that travel should be joyful from the very first click, we compare millions of options across 450+ airline partners and hundreds of thousands of properties to bring you the best price, every time.",
    "With 24/7 human support, secure payments and a price-match promise, travellers around the world trust us to power their adventures.",
    "World Tour Hub Ltd is registered in England & Wales (Company No. 17436422), with its registered office at 18 Berkeley Mews, Birmingham, West Midlands, United Kingdom, B25 8PS."] },
  contact: { title: "Contact Us", body: [
    "We're here around the clock. Reach our travel experts by phone at +44 20 7946 0000, or email hello@worldtourhub.com.",
    "Registered office: 18 Berkeley Mews, Birmingham, West Midlands, United Kingdom, B25 8PS.",
    "For existing bookings, use the Manage Booking page for the fastest service."] },
  careers: { title: "Careers", body: [
    "Help us build the world's best travel marketplace. We're hiring across engineering, design, data science and customer experience.",
    "We offer remote-first working, unlimited annual leave, generous travel credits and equity for every team member.",
    "Send your CV to careers@worldtourhub.com — we can't wait to meet you."] },
  press: { title: "Press & Media", body: [
    "For all media enquiries, interviews and brand assets, contact our press team at press@worldtourhub.com.",
    "World Tour Hub has been featured in leading travel and technology publications worldwide."] },
  help: { title: "Help Center", body: [
    "Need a hand? Most questions about changes, cancellations and refunds can be resolved instantly via Manage Booking.",
    "Our support team is available 24/7 in over 30 languages. Free cancellation applies to most flights and hotels booked on our platform.",
    "For urgent travel-day assistance, call our priority line at +44 20 7946 0000."] },
  privacy: { title: "Privacy Policy", body: [
    "Your privacy matters. World Tour Hub Ltd collects only the information necessary to process your bookings and improve your experience.",
    "We never sell your personal data. Payment details are encrypted end-to-end and handled by PCI-DSS compliant providers.",
    "You may request access to, correction of, or deletion of your data at any time by contacting privacy@worldtourhub.com.",
    "We use cookies and similar technologies to personalise content and analyse traffic. You can manage your preferences in your browser at any time.",
    "This platform complies with GDPR and applicable international data-protection regulations."] },
  terms: { title: "Terms & Conditions", body: [
    "By using World Tour Hub Ltd, you agree to these terms. All bookings are subject to the terms of the relevant airline, hotel or supplier.",
    "Prices are confirmed at the point of booking. Taxes and fees are shown clearly before you confirm.",
    "Cancellation and amendment policies vary by product and are clearly displayed prior to purchase.",
    "World Tour Hub acts as an agent for travel suppliers and is not liable for supplier-caused disruptions beyond our reasonable control.",
    "World Tour Hub Ltd is registered in England & Wales under Company No. 17436422. Registered office: 18 Berkeley Mews, Birmingham, West Midlands, United Kingdom, B25 8PS."] },
  cookies: { title: "Cookie Policy", body: [
    "We use cookies and similar technologies to make World Tour Hub work and to understand how it is used.",
    "Essential cookies are always on — they keep you signed in, remember your search and enable secure checkout. Analytics cookies are optional and only set after you accept them in our cookie banner.",
    "You can change your choice at any time by clearing your browser storage, and you can manage cookies in your browser settings. We do not send personal passenger details to analytics."] },
  cancellations: { title: "Cancellations & Amendments", body: [
    "Cancellation and amendment terms vary by flight, hotel and holiday, and by the supplier providing them. The applicable terms are shown before you book and on your confirmation.",
    "To request a change or cancellation, use Manage Booking or contact our support team with your booking reference. We will confirm what is possible and any supplier charges before proceeding.",
    "We never promise an automatic change or cancellation unless the supplier supports it. Some fares and rates are non-refundable — this is always shown clearly before payment."] },
  refunds: { title: "Refunds", body: [
    "Where a booking is refundable, refunds are processed to your original payment method once the supplier confirms cancellation. Timescales depend on the supplier and your bank.",
    "If a payment succeeds but a booking cannot be confirmed, we automatically reverse or refund the payment and notify you.",
    "You can track refund status in Manage Booking or by contacting support with your booking reference."] },
  accessibility: { title: "Accessibility", body: [
    "We want everyone to be able to plan and book their travel with ease. We aim to follow recognised accessibility guidelines, including sufficient colour contrast, clear form labels, visible error messages and full keyboard navigation.",
    "If you use assistive technology and encounter a barrier, please contact us — we will help you complete your booking and work to fix the issue.",
    "We continually review and improve the accessibility of our website."] },
};

const ADVERTISE = {
  title: "Advertise With Us",
  intro: "Reach millions of high-intent travellers actively planning their next trip. Put your brand in front of the right audience, at the right moment.",
  features: [
    { icon: Target, title: "Precision targeting", text: "Target by destination, travel dates, budget and traveller type across the entire funnel." },
    { icon: TrendingUp, title: "Google & Meta Ads ready", text: "Native integration with Google Ads, Performance Max and Meta advertising for seamless campaigns." },
    { icon: Search, title: "SEO-optimised placements", text: "Sponsored listings that rank and convert, fully indexed and search-engine friendly." },
    { icon: BarChart3, title: "Real-time analytics", text: "Live dashboards for impressions, clicks, bookings and ROAS — full transparency, always." },
  ],
};

export default function StaticPage() {
  const { slug } = useParams();

  if (slug === "advertise") {
    return (
      <div>
        <section className="bg-gradient-to-br from-brand to-[#FF0080] grain relative py-20 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center relative">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-semibold mb-5"><Megaphone className="w-4 h-4 text-brand" />For partners & advertisers</div>
            <h1 className="font-heading font-black text-4xl sm:text-6xl text-white tracking-tight">{ADVERTISE.title}</h1>
            <p className="text-lg text-slate-300 mt-5 max-w-2xl mx-auto">{ADVERTISE.intro}</p>
            <Button className="mt-8 rounded-full bg-coral hover:bg-coral-hover h-12 px-8 font-heading font-bold">Request media kit</Button>
          </div>
        </section>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {ADVERTISE.features.map((f) => (
              <div key={f.title} className="rounded-2xl bg-white border border-slate-200 p-6">
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center mb-4"><f.icon className="w-6 h-6 text-brand" /></div>
                <h3 className="font-heading font-bold text-lg text-slate-950">{f.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-14 rounded-3xl bg-brand p-10 sm:p-14 text-center relative overflow-hidden">
            <Globe2 className="w-40 h-40 text-white/10 absolute -right-6 -bottom-6" />
            <h2 className="font-heading font-black text-3xl text-white relative">Ready to reach 2.4M+ travellers?</h2>
            <p className="text-white/80 mt-3 relative">Our team will build a campaign tailored to your goals.</p>
            <Button className="mt-6 rounded-full bg-white text-brand hover:bg-slate-100 h-12 px-8 font-heading font-bold relative">Talk to our ads team</Button>
          </div>
        </section>
      </div>
    );
  }

  const c = CONTENT[slug];
  if (!c) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <h1 className="font-heading font-black text-5xl text-slate-950">404</h1>
        <p className="text-slate-500 mt-3">This page could not be found.</p>
        <Link to="/"><Button className="mt-6 rounded-full bg-brand">Back home</Button></Link>
        <div className="mt-10"><SearchWidget /></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
      <div className="flex items-center gap-2 text-brand text-sm font-bold mb-3"><ShieldCheck className="w-4 h-4" />World Tour Hub Ltd</div>
      <h1 className="font-heading font-black text-4xl text-slate-950 tracking-tight">{c.title}</h1>
      <div className="mt-6 space-y-4">
        {c.body.map((p, i) => <p key={i} className="text-slate-600 leading-relaxed text-lg">{p}</p>)}
      </div>
      <div className="mt-10"><Link to="/" className="text-brand font-bold">← Back to home</Link></div>
    </div>
  );
}
